import salaryCheckerLambdaUtil from 'salaryCheckerLambdaUtil';

const { default: hasMatch } = require('@Utils/hasMatch');
const { searchParamsToUrl } = require('@Utils/searchParams');

/**
 * Autocomplete Options validation for Occupations and Locations.
 * @param {String} occupation
 *  Occupation title
 * @param {String} location
 *  Location title
 * @param {String} language
 * @return {Promise<{occupationIsValid, locationIsValid}>}
 */
const validateAutocompleteOptions = (occupation, location, language) => Promise.all([
  occupation ? salaryCheckerLambdaUtil('occupations-autocomplete', { text: occupation, language, returnNormalizedResult: 'true' }) : null,
  location ? salaryCheckerLambdaUtil('locations-autocomplete', { text: location, language }) : null,
]).then(([occupations, locations]) => ({
  occupationIsValid: !occupation || (occupations.data.data && hasMatch(occupation, occupations.data.data, 'job_title')),
  locationIsValid: !location || (locations.data.data && hasMatch(location, locations.data.data)),
}));

/**
 * Get formatted url to redirect to salary checker app.
 *
 * @param {String} occupation
 *   Occupation field value.
 * @param {String} location
 *   Location field value.
 * @param {String} salary
 *   The salary field value.
 * @param {String} language
 *   The current langcode.
 * @param {String} salarySeparator
 *   The salary separator.
 * @returns {String}
 */
const getFormattedResultsUrl = (occupation, location, salary, language, salarySeparator) => {
  const locationArr = location.split(',');
  const urlParams = {
    occupation,
    region: locationArr.length > 1 ? locationArr.pop() : '',
    city: locationArr.join(','),
    salary: salary ? parseInt(salary.replaceAll(salarySeparator, ''), 10) : '',
  };

  return searchParamsToUrl(urlParams, language);
};

/**
 * Check salary search params before form submit.
 *
 * Rules:
 *   - occupation and location required params.
 *   - occupation and location should exist in corresponding suggestion list.
 */
const validateSearchForm = async (request) => {
  const {
    occupation = '', location = '', language = '', salary = '', sourceType = '', salarySeparator, intl,
  } = request;
  const responseData = {
    errors: {
      location: [], occupation: [],
    },
    isValid: false,
    url: '',
  };
  if (!occupation) {
    responseData.errors.occupation.push(intl.formatMessage({ id: 'Form.ErrorJob' }));
  }

  // Location required on JobAlertsForm.
  if (sourceType && sourceType === 'JobAlertsForm' && !location) {
    responseData.errors.location.push(intl.formatMessage({ id: 'Form.ErrorLocation' }));
  }

  if (occupation || location) {
    const { occupationIsValid, locationIsValid } = await validateAutocompleteOptions(occupation, location, language);

    if (!occupationIsValid) {
      responseData.errors.occupation.push(intl.formatMessage({ id: 'Form.ErrorSuggestionList' }));
    }

    if (!locationIsValid) {
      responseData.errors.location.push(intl.formatMessage({ id: 'Form.ErrorSuggestionList' }));
    }
  }

  responseData.isValid = responseData.errors.occupation.length === 0 && responseData.errors.location.length === 0;

  if (responseData.isValid) {
    responseData.url = getFormattedResultsUrl(occupation, location, salary, language, salarySeparator);
  }

  return responseData;
};

export default validateSearchForm;
